import { TFunction } from "i18next";

import {
    ChannelBundle,
    CorporateBundle,
    MobileProcessorBundle,
    MobileRetailBundle,
    ProcessorBundle,
    SmbBundle,
} from "components/licenses/bundles";
import { TableData } from "domain/table";
import { Role } from "domain/users";
import { toUtcDateString } from "utils/format";

export interface Tenant extends CommonTenant {
    countryCode: string;
    contactName: string;
    contactEmail: string;
    salesforceLeadId: string;
    expirationDate: string;
    emsId: string;
    createdTimestamp: string;
    createdBy: string;
    erasureClientEndpoint: string;
    publicApiEndpoint: string;
    usageStatistics: boolean;
    roles: Role[];
    tier: CombinedTier;
    salesforceAccountId: string;
    type: TenantType;
    totalReportsCount: number;
    totalUsers: number;
    totalWorkflows: number;
    overallTotalUsers?: number;
    overallTotalWorkflows?: number;
    overallTotalReportsCount?: number;
    productsLicensed: [];
    availableLicenses: number;
    parentExpirationDate: string;
    loginMethod?: LoginMethod;
    deletionStatus: boolean;
    tenantHierarchyLevel: number;
    tenants?: Tenant[];
    parentUuid?: string;
    foundUser?: string;
    foundUserName?: string;
    subTenantsCount: number;
    overallSubTenantsCount?: number;
    totalLogins?: number;
    overallTotalLogins?: number;
    lastLoggedInUser?: string;
    lastLogin?: string;
    licensingModel: LicensingModel;
    rateUuid?: string;
    ipRestrictions: [];
    parentLicensingModel: LicensingModel;
    parentType: TenantType;
    parentTier: CombinedTier;
    tenantName?: string;
    salesforceContractId?: string;
    salesforceContractState?: TenantContractStatus | string;
    salesforceContactEmail?: string;
    salesforceContractEndDate?: string;
    parentDrawbackPrevention: boolean;
    commonLicenseCount?: number;
    drawbackPrevention?: boolean;
    contractType: ContractType;
    parentContractType: ContractType;
    automaticDeletion?: boolean;
    depth?: number;
    refreshTokenExpiration?: number;
    newsEmailOptIn?: boolean;
    diskQuotaMib?: number;
    uploadLimitMib?: number;
    downloadLimitMib?: number;
}

export interface CommonTenant extends TableData {
    name: string;
    notes: string;
    region: string;
    uuid: string;
    status: boolean;
}

export interface TenantCursor {
    name: string;
    parentUuid: string;
}

export interface Tenants extends TableData {
    tenantList: Tenant[];
    cursor: TenantCursor | null;
}

export interface TenantsData {
    tenantList: Tenants;
    message?: boolean;
}

export interface FetchRegionsListResponse {
    regions: string[];
}

export interface TenantListItem {
    uuid: string;
    name: string;
    parentUuid: string;
    tier: CombinedTier;
    licensingModel: LicensingModel;
    type: TenantType;
    combinedDepthExceeded?: boolean;
    parentLevel?: number;
}

export const ssoLoginMethodList = ["COMPANY_ID"];
export const loginMethodList = [...ssoLoginMethodList, "EMAIL", "COMPANY_ID_AND_EMAIL"];
export type LoginMethod = (typeof loginMethodList)[number];
export type ContractType = "TRIAL" | "PRODUCTION";
export type TenantType =
    "INTERNAL" | "CUSTOMER" | "CHANNEL" | "CORPORATE" | "PROCESSOR" | "SMB" | "MOBILE_RETAIL" | "MOBILE_PROCESSOR";
export const CONTRACT_TYPE_TRIAL = "TRIAL";
export const CONTRACT_TYPE_PRODUCTION = "PRODUCTION";
export const TENANT_TYPES: TenantType[] = ["CHANNEL", "CORPORATE", "CUSTOMER", "PROCESSOR", "SMB"];
export enum TenantContractStatus {
    SELECT = "SELECT",
    DRAFT = "DRAFT",
    ACTIVE = "ACTIVE",
    EXPIRED = "EXPIRED",
    TERMINATED = "TERMINATED",
}
export enum LicensingModel {
    OLD_MODEL = "OLD_MODEL",
    BUNDLE = "BUNDLE",
    BUNDLE_WITH_TOKEN = "BUNDLE_WITH_TOKEN",
}
export function contractTypeToTranslationMap(t: TFunction) {
    return new Map<ContractType, string>([
        ["TRIAL", t("Common.trialTenantType")],
        ["PRODUCTION", t("Common.productionTenantType")],
    ]);
}
export function generateContractTypes(): ContractType[] {
    return ["TRIAL", "PRODUCTION"];
}
export const INTERNAL_TENANT_UUID = "BLANCCO";
export enum OldLicensingModelTier {
    BASIC = "BASIC",
    STANDARD = "STANDARD",
    ADVANCED = "ADVANCED",
}
export const COMBINED_TIERS = Object.keys(OldLicensingModelTier)
    .concat(Object.keys(CorporateBundle))
    .concat(Object.keys(ProcessorBundle))
    .concat(Object.keys(SmbBundle))
    .concat(Object.keys(ChannelBundle))
    .concat(Object.keys(MobileRetailBundle))
    .concat(Object.keys(MobileProcessorBundle));

export type CombinedTier = (typeof COMBINED_TIERS)[number];

export function showDaysTillDeletion(contractType: ContractType, automaticDeletion?: boolean) {
    return contractType === "TRIAL" && automaticDeletion;
}

export function countDaysTillDeletion(expirationDateAsString: string): number {
    const nintyDaysAfterExpiration = new Date(expirationDateAsString);
    nintyDaysAfterExpiration.setDate(nintyDaysAfterExpiration.getUTCDate() + 90);
    const msDay = 24 * 60 * 60 * 1000;
    return Math.ceil((nintyDaysAfterExpiration.getTime() - new Date().getTime()) / msDay);
}

export const calculateTrialDaysLeft = (tenant: Tenant) => {
    const tenantExpirationDate = tenant.expirationDate;
    if (!tenantExpirationDate) {
        return null;
    }

    const trialEndDate = new Date(tenantExpirationDate);
    const today = new Date();
    const trialEndDateUtc = new Date(toUtcDateString(trialEndDate));
    const nowToUtc = new Date(toUtcDateString(today));

    const calculateDifference = trialEndDateUtc.getTime() - nowToUtc.getTime();

    return Math.ceil(calculateDifference / (1000 * 60 * 60 * 24));
};

export function isMobileRetail(tenantType: CombinedTier) {
    return tenantType === "MOBILE_RETAIL";
}
